import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Each screen/domain owns one JSON file per language. `common` is spread at
// the root so shared keys read as t('actions.save'); every other file is
// namespaced under its own key, e.g. t('auth.register.title'),
// t('profile.signOut'). Adding a language = add a folder here + a case in
// SUPPORTED_LANGUAGES (keep in sync with the backend's auth.dto).
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enProfile from './locales/en/profile.json';
import enHome from './locales/en/home.json';
import enGames from './locales/en/games.json';
import enTopup from './locales/en/topup.json';
import enWallet from './locales/en/wallet.json';
import enWithdraw from './locales/en/withdraw.json';
import enNotifications from './locales/en/notifications.json';
import enNotificationPrefs from './locales/en/notificationPrefs.json';
import enOrderTracking from './locales/en/ordertracking.json';
import enFaq from './locales/en/faq.json';
import enHelp from './locales/en/help.json';
import enInstall from './locales/en/install.json';
import enComponents from './locales/en/components.json';

import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esProfile from './locales/es/profile.json';
import esHome from './locales/es/home.json';
import esGames from './locales/es/games.json';
import esTopup from './locales/es/topup.json';
import esWallet from './locales/es/wallet.json';
import esWithdraw from './locales/es/withdraw.json';
import esNotifications from './locales/es/notifications.json';
import esNotificationPrefs from './locales/es/notificationPrefs.json';
import esOrderTracking from './locales/es/ordertracking.json';
import esFaq from './locales/es/faq.json';
import esHelp from './locales/es/help.json';
import esInstall from './locales/es/install.json';
import esComponents from './locales/es/components.json';

export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const LANGUAGE_STORAGE_KEY = 'app_language';
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export const LANGUAGE_OPTIONS: { code: SupportedLanguage; label: string; nativeLabel: string; flag: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', flag: '🇪🇸' },
];

function buildResources(
  common: object,
  auth: object, profile: object, home: object, games: object, topup: object,
  wallet: object, withdraw: object, notifications: object, notificationPrefs: object,
  ordertracking: object, faq: object, help: object, install: object, components: object,
) {
  return {
    translation: {
      ...common,
      auth, profile, home, games, topup, wallet, withdraw,
      notifications, notificationPrefs, ordertracking, faq, help, install, components,
    },
  };
}

export function normalizeLanguage(value: string | null | undefined): SupportedLanguage {
  const lang = (value || '').toLowerCase().split('-')[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(lang)
    ? (lang as SupportedLanguage)
    : DEFAULT_LANGUAGE;
}

function initialLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) return normalizeLanguage(stored);
    return normalizeLanguage(navigator.language);
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

i18n.use(initReactI18next).init({
  resources: {
    en: buildResources(
      enCommon, enAuth, enProfile, enHome, enGames, enTopup, enWallet, enWithdraw,
      enNotifications, enNotificationPrefs, enOrderTracking, enFaq, enHelp, enInstall, enComponents,
    ),
    es: buildResources(
      esCommon, esAuth, esProfile, esHome, esGames, esTopup, esWallet, esWithdraw,
      esNotifications, esNotificationPrefs, esOrderTracking, esFaq, esHelp, esInstall, esComponents,
    ),
  },
  lng: initialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: { escapeValue: false },
  returnNull: false,
});

export default i18n;
